import { Button, Menu, MenuButton, MenuList } from "@chakra-ui/react";
import { fetchFresh } from "../../common/redux/fetchFresh";
import { useTranslation } from "react-i18next";
import { FiChevronDown } from "react-icons/fi";
import { bffApi, type ListBuyerEventsApiArg } from "../../autogen/bff-api";
import { ListDownloadItems, listSelection } from "../../common/download/ListDownloadItems";
import {
  exportTooLarge,
  exportRequest,
  fetchAllPages,
  pdfTooLarge,
  type ExportColumn,
} from "../../common/download/listExport";
import { useAppDispatch } from "../../common/redux/hooks";
import { useProcurementTerms } from "../../common/terminology/useProcurementTerms";
import { procurementCell, procurementListFileName } from "./procurementListExport";

export const ProcurementListDownloadMenu = ({
  count,
  isLoading,
  failed,
  columns,
  query,
  filters,
}: {
  count: number | undefined;
  isLoading: boolean;
  failed: boolean;
  columns: ExportColumn[];
  query: ListBuyerEventsApiArg;
  filters: string[];
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const terms = useProcurementTerms();
  const tooMany = exportTooLarge(count ?? 0, columns.length);
  const tooManyForPdf = pdfTooLarge(count ?? 0, columns.length);
  const listNotReady = isLoading || failed || count === undefined;

  const buildBody = async () => {
    const events = await fetchAllPages(async (skip, limit) => {
      const page = await fetchFresh(
        dispatch(bffApi.endpoints.listBuyerEvents.initiate({ ...query, skip, limit }, { forceRefetch: true })),
      );
      return page.events;
    });
    const ctx = { t, publishedLabel: terms.publishedStatus };
    const selection = listSelection(t, events.length, filters);
    return exportRequest(columns, events, (key, event) => procurementCell(key, event, ctx), selection);
  };

  return (
    <Menu isLazy closeOnSelect={false} placement="bottom-end">
      {({ onClose }) => (
        <>
          <MenuButton as={Button} size="sm" variant="outline" rightIcon={<FiChevronDown />}>
            {t("Download")}
          </MenuButton>
          <MenuList>
            <ListDownloadItems
              label={t("Procurement list")}
              endpoint={`/api/buyer/${query.orgId}/events/list-export`}
              baseFileName={procurementListFileName(new Date())}
              errorMessage={String(t("The procurement list could not be downloaded. Try again."))}
              buildBody={buildBody}
              isLoading={isLoading}
              isDisabled={listNotReady || count === 0 || tooMany}
              hint={
                !listNotReady && count === 0
                  ? String(t("No procurements to download"))
                  : !listNotReady && tooMany
                    ? String(t("The list has too many procurements for one file. Narrow it with the filters."))
                    : undefined
              }
              pdfHint={
                tooManyForPdf
                  ? String(t("Too many rows for a PDF. Choose Excel or CSV, or narrow the list with the filters."))
                  : undefined
              }
              onDone={onClose}
            />
          </MenuList>
        </>
      )}
    </Menu>
  );
};
